// 機關時間軸（像 Cinema 4D 的 Timeline）的純邏輯：畫面在 components/mechanisms/MechanismTimeline.tsx，算術都在這裡，有測試。
//   key = 一段動作（TimelineMove）的開始 / 結束；只是「延遲 / 走幾秒」的另一種畫法，不是新的資料
//     開始 key 的時間 = GO + 延遲；結束 key 的時間 = 開始 + 走幾秒
//   拖開始 key = 改延遲（結束跟著平移）；拖結束 key = 改走幾秒
//   整批：碰到邊界（延遲或走幾秒到 0）整批停住，不壓扁；放開才交給頁面（一步復原）
//   cue 層級：拖編號改時間（可以越過別的 cue，編號依時間重排）、拉轉場條（GO = max(到位 − 轉場, 上一個到位)）

import { formatMechValue, formatSeconds } from '../mechanisms/motion';
import type { PickHow, TimingChange } from '../mechanisms/timelineEdit';
import { cueWindows } from '../cueTimeline';
import { cueTransitionSeconds } from '../cueTiming';

export const TIMELINE_TEXT = {
  /** 開始 key 碰到這個 cue 的開始轉場：不是錯誤，是提示 */
  goLimit: '可以先把轉場拉長',
} as const;

const MAX_SECONDS = 3600;
/** 範圍條最少留這麼長，不能壓扁 */
export const MIN_RANGE_SECONDS = 0.1;
/** 兩個 cue 最少隔這麼遠（後台不收同一時間；放開時撞到就往拖動方向推過去） */
export const MIN_CUE_GAP = 0.1;

const round3 = (v: number) => {
  const r = Math.round(v * 1000) / 1000;
  return Object.is(r, -0) ? 0 : r;
};

/** 吸附 0.1 秒；按住 Alt 不吸附（只修掉小數點雜訊） */
export const snapTime = (v: number, alt: boolean) => (alt ? round3(v) : Math.round(v * 10) / 10);

// ─────────────────────────────────────────────
// key ↔ 延遲 / 走幾秒
// ─────────────────────────────────────────────

export type KeyEnd = 'begin' | 'end';
export interface KeyRef { cueId: string; mechId: string; end: KeyEnd }
export interface KeyTiming { go: number; delay: number; duration: number }

const SEP = '\u0000';
export const moveId = (cueId: string, mechId: string) => `${cueId}${SEP}${mechId}`;
export const keyId = (k: KeyRef) => `${k.cueId}${SEP}${k.mechId}${SEP}${k.end}`;
export const keyIdsOfMove = (cueId: string, mechId: string) => [keyId({ cueId, mechId, end: 'begin' }), keyId({ cueId, mechId, end: 'end' })];
export function parseKeyId(id: string): KeyRef | null {
  const [cueId, mechId, end] = id.split(SEP);
  return mechId !== undefined && (end === 'begin' || end === 'end') ? { cueId, mechId, end } : null;
}

export const beginTime = (t: KeyTiming) => t.go + t.delay;
export const endTime = (t: KeyTiming) => t.go + t.delay + t.duration;
/** 開始 key 放到第 t 秒 → 延遲（不能早於 GO） */
export const delayFromBegin = (go: number, t: number) => Math.max(0, round3(t - go));
/** 結束 key 放到第 t 秒 → 走幾秒（不能早於開始） */
export const durationFromEnd = (begin: number, t: number) => Math.max(0, round3(t - begin));

// ─────────────────────────────────────────────
// 整批：平移
// ─────────────────────────────────────────────

/** 一段動作在這一批裡的狀態：哪個 key 被選到 */
export interface BatchMove extends KeyTiming { cueId: string; mechId: string; begin: boolean; end: boolean }
/** 預覽 / 結果：一段動作的新時間（key = moveId） */
export type TimingMap = Map<string, { delay: number; duration: number }>;
/**
 * 'all'  = 整批一起動（拖開始 key、色塊、範圍條中間）：開始 key 選到 → 改延遲（結束跟著）；只選到結束 key → 改走幾秒
 * 'ends' = 拖結束 key：只有選到的結束 key 一起改同樣的量（只選到開始 key 的那段不動）
 */
export type BatchMode = 'all' | 'ends';

/** 選到的 key → 這一批。只算 cueId 這個 cue（別的 cue 看得到、選得到，但拖不動）；cueId 沒給 = 全部 */
export function batchOf(
  moves: readonly (KeyTiming & { cueId: string; mechId: string })[],
  selected: ReadonlySet<string>,
  cueId?: string | null,
): BatchMove[] {
  const out: BatchMove[] = [];
  for (const m of moves) {
    if (cueId !== undefined && m.cueId !== cueId) continue;
    const [b, e] = keyIdsOfMove(m.cueId, m.mechId);
    const begin = selected.has(b), end = selected.has(e);
    if (begin || end) out.push({ cueId: m.cueId, mechId: m.mechId, go: m.go, delay: m.delay, duration: m.duration, begin, end });
  }
  return out;
}

const movesDelay = (m: BatchMove, mode: BatchMode) => mode === 'all' && m.begin;
const movesDuration = (m: BatchMove, mode: BatchMode) => (mode === 'ends' ? m.end : m.end && !m.begin);

/** 整批能移動的範圍（秒）；minIsGo = 下限是某個開始 key 碰到 GO */
export function shiftBounds(items: readonly BatchMove[], mode: BatchMode): { min: number; max: number; minIsGo: boolean } {
  let minDelay = Infinity, minDur = Infinity, maxV = 0;
  for (const m of items) {
    if (movesDelay(m, mode)) { minDelay = Math.min(minDelay, m.delay); maxV = Math.max(maxV, m.delay); }
    if (movesDuration(m, mode)) { minDur = Math.min(minDur, m.duration); maxV = Math.max(maxV, m.duration); }
  }
  const room = Math.min(minDelay, minDur);
  return { min: room === Infinity ? 0 : -room, max: MAX_SECONDS - maxV, minIsGo: minDelay !== Infinity && minDelay <= minDur };
}

export function shiftBatch(items: readonly BatchMove[], delta: number, mode: BatchMode): TimingMap {
  const out: TimingMap = new Map();
  for (const m of items) {
    const d = movesDelay(m, mode), r = movesDuration(m, mode);
    if (!d && !r) continue;
    out.set(moveId(m.cueId, m.mechId), {
      delay: d ? Math.max(0, round3(m.delay + delta)) : m.delay,
      duration: r ? Math.max(0, round3(m.duration + delta)) : m.duration,
    });
  }
  return out;
}

/**
 * 拖曳的量 → 實際移動的量：被拖的那個值（anchor：延遲或走幾秒；範圍條中間給 0）吸附後算出差，再照整批的邊界停住。
 * stoppedAtGo = 想拖到比 GO 更早（要顯示「可以先把轉場拉長」）
 */
export function dragShift(items: readonly BatchMove[], mode: BatchMode, anchor: number, raw: number, alt: boolean): { delta: number; stoppedAtGo: boolean } {
  const b = shiftBounds(items, mode);
  const want = round3(snapTime(anchor + raw, alt) - anchor);
  const delta = round3(Math.max(b.min, Math.min(b.max, want)));
  return { delta: Object.is(delta, -0) ? 0 : delta, stoppedAtGo: want < b.min && b.minIsGo };
}

/** 放開時交給頁面的改動：只放真的變了的欄位；cueId 給舊路徑（一格一格存）用，導演頁的 onTimings 不用它 */
export function changesFrom(items: readonly BatchMove[], next: TimingMap): (TimingChange & { cueId: string })[] {
  const out: (TimingChange & { cueId: string })[] = [];
  for (const m of items) {
    const n = next.get(moveId(m.cueId, m.mechId));
    if (!n) continue;
    const c: TimingChange & { cueId: string } = { cueId: m.cueId, mechId: m.mechId };
    if (Math.abs(n.delay - m.delay) > 1e-6) c.delaySec = n.delay;
    if (Math.abs(n.duration - m.duration) > 1e-6) c.durationSec = n.duration;
    if (c.delaySec !== undefined || c.durationSec !== undefined) out.push(c);
  }
  return out;
}

// ─────────────────────────────────────────────
// 範圍條：依比例縮放
// ─────────────────────────────────────────────

/** 選到的 key 最早到最晚；只有一個時間（或沒有）→ null，不畫範圍條 */
export function selectionRange(items: readonly BatchMove[]): { min: number; max: number } | null {
  let min = Infinity, max = -Infinity;
  for (const m of items) {
    if (m.begin) { const t = beginTime(m); min = Math.min(min, t); max = Math.max(max, t); }
    if (m.end) { const t = endTime(m); min = Math.min(min, t); max = Math.max(max, t); }
  }
  return max - min > 1e-6 ? { min, max } : null;
}

/** 以 anchor 為固定點，比例 k 能到哪：開始 key 不早於 GO、只選到結束 key 的不早於自己的開始、整條不壓扁 */
export function scaleBounds(items: readonly BatchMove[], anchor: number, rangeLen: number): { min: number; max: number } {
  let min = rangeLen > 0 ? MIN_RANGE_SECONDS / rangeLen : 0, max = Infinity;
  // c0 + c1 × k ≥ 0
  const limit = (c0: number, c1: number) => {
    if (c1 > 1e-9) min = Math.max(min, -c0 / c1);
    else if (c1 < -1e-9) max = Math.min(max, -c0 / c1);
  };
  for (const m of items) {
    if (m.begin) limit(anchor - m.go, beginTime(m) - anchor);
    else if (m.end) limit(anchor - beginTime(m), endTime(m) - anchor);
  }
  return { min: Math.min(min, 1), max: Math.max(max, 1) };
}

export function scaleBatch(items: readonly BatchMove[], anchor: number, k: number): TimingMap {
  const out: TimingMap = new Map();
  for (const m of items) {
    const b0 = beginTime(m), e0 = endTime(m);
    const b = m.begin ? anchor + (b0 - anchor) * k : b0;
    // 只選到開始 key：走幾秒不變（結束跟著）；有選到結束 key：照比例
    const e = m.end ? anchor + (e0 - anchor) * k : b + m.duration;
    out.set(moveId(m.cueId, m.mechId), { delay: delayFromBegin(m.go, b), duration: durationFromEnd(b, e) });
  }
  return out;
}

/** 拖範圍條的把手：另一端固定；被拖的那端吸附；碰到邊界整批停住。回傳比例、固定點、被拖那端的時間 */
export function dragScale(
  items: readonly BatchMove[],
  range: { min: number; max: number },
  side: 'left' | 'right',
  raw: number,
  alt: boolean,
): { k: number; anchor: number; edge: number } {
  const len = range.max - range.min;
  const anchor = side === 'right' ? range.min : range.max;
  const edge0 = side === 'right' ? range.max : range.min;
  const want = snapTime(edge0 + raw, alt);
  const wantK = side === 'right' ? (want - anchor) / len : (anchor - want) / len;
  const b = scaleBounds(items, anchor, len);
  const k = Math.max(b.min, Math.min(b.max, wantK));
  return { k, anchor, edge: round3(side === 'right' ? anchor + len * k : anchor - len * k) };
}

// ─────────────────────────────────────────────
// 框選（資料座標：時間 + 第幾排，捲出畫面的 key 也算得到）
// ─────────────────────────────────────────────

export interface PlacedKey extends KeyRef { time: number; row: number }
export interface MarqueeBox { x0: number; y0: number; x1: number; y1: number }
export interface KeyGeometry { pad: number; pps: number; rowH: number; /** key 半徑（px）：框碰到 key 的邊就算 */ hit?: number }

/** 框（內容區的 px）蓋到哪些 key */
export function keysInBox(keys: readonly PlacedKey[], box: MarqueeBox, g: KeyGeometry): PlacedKey[] {
  const hit = g.hit ?? 5;
  const left = Math.min(box.x0, box.x1), right = Math.max(box.x0, box.x1);
  const top = Math.min(box.y0, box.y1), bottom = Math.max(box.y0, box.y1);
  return keys.filter(k => {
    const x = g.pad + k.time * g.pps;
    const y = (k.row + 0.5) * g.rowH;
    return x + hit >= left && x - hit <= right && y + hit >= top && y - hit <= bottom;
  });
}

export type MarqueeHow = 'replace' | 'add' | 'remove';
export function combineSelection(base: ReadonlySet<string>, hits: Iterable<string>, how: MarqueeHow): Set<string> {
  const next = how === 'replace' ? new Set<string>() : new Set(base);
  for (const id of hits) { if (how === 'remove') next.delete(id); else next.add(id); }
  return next;
}

const mechsOf = (sel: ReadonlySet<string>) => {
  const s = new Set<string>();
  for (const id of sel) { const k = parseKeyId(id); if (k) s.add(k.mechId); }
  return s;
};

/** key 的選取變了 → 要跟頁面說哪些機關（一個機關還有別的 key 被選著就不算減掉） */
export function pickCalls(before: ReadonlySet<string>, after: ReadonlySet<string>, replace: boolean): { ids: string[]; how: PickHow }[] {
  const a = mechsOf(after);
  if (replace) return [{ ids: [...a], how: 'replace' }];
  const b = mechsOf(before);
  const added = [...a].filter(id => !b.has(id));
  const removed = [...b].filter(id => !a.has(id));
  const out: { ids: string[]; how: PickHow }[] = [];
  if (removed.length) out.push({ ids: removed, how: 'remove' });
  if (added.length) out.push({ ids: added, how: 'add' });
  return out;
}

/** 拖到邊緣自動捲：離邊緣 zone px 內開始，越靠近（或超出）越快；回傳這一幀捲幾 px（負 = 往前） */
export function edgeScrollSpeed(pos: number, lo: number, hi: number, zone = 24, maxSpeed = 14): number {
  if (hi <= lo) return 0;
  const part = (d: number) => Math.min(1, d / zone) * maxSpeed;
  if (pos < lo + zone) return -Math.ceil(part(lo + zone - pos));
  if (pos > hi - zone) return Math.ceil(part(pos - (hi - zone)));
  return 0;
}

/** 總覽列的小 key：底下所有 key 彙總；疊在同一個 px 的併成一個（有一個選到就算選到） */
export function summaryKeys(keys: readonly { time: number; selected: boolean }[], pps: number): { time: number; selected: boolean }[] {
  const byPx = new Map<number, { time: number; selected: boolean }>();
  for (const k of keys) {
    const px = Math.round(k.time * pps);
    const cur = byPx.get(px);
    if (cur) cur.selected = cur.selected || k.selected;
    else byPx.set(px, { time: k.time, selected: k.selected });
  }
  return [...byPx.values()].sort((a, b) => a.time - b.time);
}

// ─────────────────────────────────────────────
// cue 編號：拖動
// ─────────────────────────────────────────────

/** 整批 cue 能移動的範圍：不能小於 0 秒、不能超過上限；可以越過別的 cue（編號會依時間重排） */
export function cueMoveBounds(cues: readonly { id: string; time: number }[], ids: ReadonlySet<string>): { min: number; max: number } {
  let lo = Infinity, hi = -Infinity;
  for (const c of cues) {
    if (!ids.has(c.id)) continue;
    lo = Math.min(lo, c.time);
    hi = Math.max(hi, c.time);
  }
  if (lo === Infinity) return { min: 0, max: 0 };
  return { min: Math.min(0, round3(-lo)), max: Math.max(0, round3(MAX_SECONDS - hi)) };
}

/**
 * 整批移動 delta 秒之後，有沒有哪個選到的 cue 跟「沒選到的」cue 太近（後台不收兩個 cue 同一時間）。
 * 回 dir 方向要再推多少才剛好離開所有撞到的 cue；沒撞到 → null
 */
function pushOut(
  cues: readonly { id: string; time: number }[],
  ids: ReadonlySet<string>,
  delta: number,
  dir: 1 | -1,
): number | null {
  let next: number | null = null;
  for (const c of cues) {
    if (!ids.has(c.id)) continue;
    const t = c.time + delta;
    for (const u of cues) {
      if (ids.has(u.id) || Math.abs(t - u.time) >= MIN_CUE_GAP - 1e-6) continue;
      const want = round3(u.time + dir * MIN_CUE_GAP - c.time);
      next = next === null ? want : dir === 1 ? Math.max(next, want) : Math.min(next, want);
    }
  }
  return next;
}

/**
 * 想移動 want 秒 → 實際移動的量：先照 0 秒 / 上限停住；落點跟沒選到的 cue 距離小於 MIN_CUE_GAP 時，
 * 往拖動方向推到剛好超過它（推不過去 = 到了邊界，就反方向找最近的空位）；找不到 → 0
 */
export function resolveCueDelta(
  cues: readonly { id: string; time: number }[],
  ids: ReadonlySet<string>,
  want: number,
): number {
  const b = cueMoveBounds(cues, ids);
  const clamp = (d: number) => round3(Math.max(b.min, Math.min(b.max, d)));
  const start = clamp(want);
  const search = (dir: 1 | -1): number | null => {
    let d = start;
    for (let i = 0; i < cues.length * 2 + 4; i++) {
      const p = pushOut(cues, ids, d, dir);
      if (p === null) return d;
      if (dir === 1 ? p > b.max + 1e-9 : p < b.min - 1e-9) return null;
      d = p;
    }
    return null;
  };
  const first: 1 | -1 = want >= 0 ? 1 : -1;
  const r = search(first) ?? search(first === 1 ? -1 : 1);
  return r === null ? 0 : (Object.is(r, -0) ? 0 : r);
}

/** 拖 cue 編號：被拖的那個吸附，整批維持間隔；可以越過別的 cue，不能小於 0 秒；落點撞到別的 cue 就推到剛好過去 */
export function cueMoveResult(
  cues: readonly { id: string; time: number }[],
  ids: ReadonlySet<string>,
  anchorId: string,
  raw: number,
  alt: boolean,
): { delta: number; changes: { cueId: string; videoTimeSec: number }[] } {
  const anchor = cues.find(c => c.id === anchorId)?.time ?? 0;
  const delta = resolveCueDelta(cues, ids, round3(snapTime(anchor + raw, alt) - anchor));
  if (Math.abs(delta) < 1e-6) return { delta: 0, changes: [] };
  return { delta, changes: cueChanges(cues, ids, delta) };
}

/** 放開時交給頁面的：這些 cue 的新時間 */
export function cueChanges(cues: readonly { id: string; time: number }[], ids: ReadonlySet<string>, delta: number): { cueId: string; videoTimeSec: number }[] {
  return cues.filter(c => ids.has(c.id)).map(c => ({ cueId: c.id, videoTimeSec: round3(c.time + delta) }));
}

// ─────────────────────────────────────────────
// 拖 cue 編號：色塊整組跟著走
// ─────────────────────────────────────────────

/** 一個有影片時間的 cue：時間、這首歌用的轉場秒數 */
export interface CarryCue { id: string; time: number; crossfadeSeconds: number }
export interface CarryWindow { cueId: string; start: number; arrive: number }
export interface CarryChange { cueId: string; videoTimeSec: number; /** 有值 = 這個 cue 的轉場被上一個 cue 夾住，要一起存成實際走的秒數（只改這首歌） */ crossfadeSeconds?: number }

/**
 * 每個 cue 的 GO / 到位（跟 buildMechanismTimeline 一樣的算法）。
 * hasStart = 這首歌有起始位置（機關時間軸的第一個 cue 也從起始位置走過來，GO = 到位 − 轉場，最早 0 秒）
 */
export function carryWindows(cues: readonly CarryCue[], hasStart: boolean): CarryWindow[] {
  const byId = new Map(cues.map(c => [c.id, c]));
  const wins = cueWindows(cues.map(c => ({ id: c.id, videoTimeSec: c.time, crossfadeSeconds: c.crossfadeSeconds })));
  if (hasStart && wins.length) {
    const w0 = wins[0];
    wins[0] = { ...w0, start: Math.max(0, w0.arrive - cueTransitionSeconds(byId.get(w0.cueId))) };
  }
  return wins;
}

/**
 * 拖 cue 編號 = 這個 cue 的色塊整組跟著走。
 * 轉場比兩個 cue 的間隔長時，GO 被上一個 cue 的到位夾住（實際走的秒數 < 設定的轉場）；
 * 只平移到位時間，放開後 GO 還是夾在原處，色塊就跑回去。所以被夾住的 cue 要把轉場秒數一起存成實際走的秒數，GO 才會跟著平移。
 * 沒被夾住的只改時間。第一個 cue 沒有色塊（機關時間軸沒有起始位置時）→ 不動它的轉場。
 *
 * before / after 是同一個算法算的，預覽（拖到一半）和放開後完全一致：
 * 越過別的 cue 後 GO 又被新鄰居夾住，預覽也一樣。
 */
export function carryCueMove(
  cues: readonly CarryCue[],
  ids: ReadonlySet<string>,
  delta: number,
  hasStart: boolean,
): { changes: CarryChange[]; before: CarryWindow[]; after: CarryWindow[]; shifts: Map<string, { start: number; arrive: number }> } {
  const before = carryWindows(cues, hasStart);
  const win = new Map(before.map(w => [w.cueId, w]));
  const index = new Map(before.map((w, i) => [w.cueId, i]));
  const changes: CarryChange[] = [];
  for (const c of cues) {
    if (!ids.has(c.id)) continue;
    const ch: CarryChange = { cueId: c.id, videoTimeSec: round3(c.time + delta) };
    const w = win.get(c.id);
    if (w && (hasStart || (index.get(c.id) ?? 0) > 0)) {
      const span = round3(w.arrive - w.start);
      // 走 0 秒 = 本來就沒有色塊可帶（排在最前面沒地方出發）；搬到後面轉場照原本的設定
      if (span > 1e-6 && span < cueTransitionSeconds(c) - 1e-6) ch.crossfadeSeconds = span;
    }
    changes.push(ch);
  }
  const build = (list: readonly CarryChange[]) => {
    const byId = new Map(list.map(c => [c.cueId, c]));
    return carryWindows(cues.map(c => {
      const ch = byId.get(c.id);
      return ch ? { id: c.id, time: ch.videoTimeSec, crossfadeSeconds: ch.crossfadeSeconds ?? c.crossfadeSeconds } : c;
    }), hasStart);
  };
  // 只在「不存轉場 GO 就不會跟著走」的時候才存（被夾住的 cue 往左拖，GO 本來就留在上一個 cue 的到位，不用動轉場）
  const carried = build(changes);
  const plain = build(changes.map(c => ({ cueId: c.cueId, videoTimeSec: c.videoTimeSec })));
  const plainStart = new Map(plain.map(w => [w.cueId, w.start]));
  for (const ch of changes) {
    if (ch.crossfadeSeconds === undefined) continue;
    const a = carried.find(w => w.cueId === ch.cueId)?.start, b = plainStart.get(ch.cueId);
    if (a !== undefined && b !== undefined && Math.abs(a - b) < 1e-6) delete ch.crossfadeSeconds;
  }
  const after = build(changes);
  const shifts = new Map<string, { start: number; arrive: number }>();
  for (const w of after) {
    const o = win.get(w.cueId);
    if (o) shifts.set(w.cueId, { start: round3(w.start - o.start), arrive: round3(w.arrive - o.arrive) });
  }
  return { changes, before, after, shifts };
}

// ─────────────────────────────────────────────
// 轉場條
// ─────────────────────────────────────────────

/** 轉場秒數的上下限：下限 0；上限碰到上一個 cue 的到位；第一個 cue（沒有上一個）到 0:00 */
export function transitionLimits(arrive: number, prevArrive: number | null): { min: number; max: number } {
  return { min: 0, max: Math.max(0, round3(arrive - (prevArrive ?? 0))) };
}

/** 拖轉場條左端：編號（到位）不動，只動開始時間；吸附 0.1 秒 */
export function transitionFromDrag(arrive: number, go0: number, prevArrive: number | null, raw: number, alt: boolean): number {
  const lim = transitionLimits(arrive, prevArrive);
  return Math.max(lim.min, Math.min(lim.max, snapTime(arrive - (go0 + raw), alt)));
}

interface ScalableMove extends KeyTiming { mechId: string; explicitDelay: boolean; explicitDuration: boolean }

/** 自己設了延遲 / 走幾秒的色塊依比例縮放（新值 = 舊值 × 新轉場 ÷ 舊轉場；舊轉場 0 → 不動）；跟著 cue 的本來就會變，不用放 */
export function transitionScale(moves: readonly ScalableMove[], oldSec: number, newSec: number): TimingChange[] {
  if (!(oldSec > 0) || Math.abs(oldSec - newSec) < 1e-6) return [];
  const r = newSec / oldSec;
  const out: TimingChange[] = [];
  for (const m of moves) {
    if (!m.explicitDelay && !m.explicitDuration) continue;
    const c: TimingChange = { mechId: m.mechId };
    if (m.explicitDelay) c.delaySec = round3(m.delay * r);
    if (m.explicitDuration) c.durationSec = round3(m.duration * r);
    out.push(c);
  }
  return out;
}

/** 拖轉場條的預覽：這一段動作會變成怎樣（scalable = 這個 cue 能改，自己設的值才跟著比例） */
export function transitionMovePreview(
  mv: ScalableMove & { arrive: number },
  newSec: number,
  oldSec: number,
  scalable: boolean,
): KeyTiming {
  const r = scalable && oldSec > 0 ? newSec / oldSec : 1;
  return {
    go: mv.arrive - newSec,
    delay: mv.explicitDelay ? round3(mv.delay * r) : mv.delay,
    duration: mv.explicitDuration ? round3(mv.duration * r) : newSec,
  };
}

/** 上一個 cue 的到位時間（照時間排好的 windows）；第一個 → null */
export function prevArriveOf(windows: readonly { cueId: string; arrive: number }[], cueId: string): number | null {
  const i = windows.findIndex(w => w.cueId === cueId);
  return i > 0 ? windows[i - 1].arrive : null;
}

/** 歌曲長度的把手：吸附 1 秒、最短 1 秒 */
export const lengthFromDrag = (t: number) => Math.max(1, Math.round(t));

// ─────────────────────────────────────────────
// 重疊的說明（說明不是警告）
// ─────────────────────────────────────────────

export interface CutLike { byCueId: string; at: number; reached: number; started: boolean }
export type CutState = 'cut' | 'cancelled' | null;

/** 'cut' 走到一半被接手；'cancelled' 還沒開始就被取消 */
export function cutStateOf(mv: { end: number; cut: CutLike | null }): CutState {
  if (!mv.cut) return null;
  if (!mv.cut.started) return 'cancelled';
  return mv.end > mv.cut.at ? 'cut' : null;
}

export function overlapText(mv: { end: number; cut: CutLike | null }, kind: 'linear' | 'rotary', cueName: (id: string) => string): string {
  const s = cutStateOf(mv);
  if (!s || !mv.cut) return '';
  const by = cueName(mv.cut.byCueId);
  return s === 'cancelled' ? `還沒開始就被 ${by} 取消` : `走到一半被 ${by} 接手，停在 ${formatMechValue(mv.cut.reached, kind)}`;
}

/** 轉場條的字：被上一個 cue 擋短 → 寫設定和實際 */
export function transitionText(setSec: number, actualSec: number): { shortened: boolean; text: string } {
  const shortened = setSec - actualSec > 0.05;
  return {
    shortened,
    text: shortened ? `設定 ${formatSeconds(setSec)} 秒，實際 ${formatSeconds(actualSec)} 秒` : `轉場 ${formatSeconds(actualSec)} 秒`,
  };
}
